import { ErrorIcon } from "components/atoms/Icon";
import { Header, Paragraph } from "components/atoms/Typography";
import FormInput from "components/molecules/FormInput";
import { getDisplayInfo } from "utils/formatting";
import BuildingTypeDropdown from "components/molecules/BuildingTypeDropdown";
import classNames from "classnames";
import FootprintsMap from "components/molecules/FootprintsMap";
import { getValueForKey, valueIsValid } from "../BuildingSidebarTabs/BuildingConstruction.helpers";
import { BuildingPropertyField, BuildingSectionProps } from "../BuildingConstruction.constants";

interface BuildingSectionBodyProps extends BuildingSectionProps {
    fields: BuildingPropertyField[];
}

function BuildingSectionBody({
    location,
    buildingConstruction,
    buildingUid,
    setProperties,
    isEditing,
    fields,
    buildingFootprints,
    selectedFootprintUid,
    updateValidationMapForKey,
    selectedFootprintPolygon,
    setSelectedFootprintPolygon,
}: BuildingSectionBodyProps) {
    return (
        <div className={classNames("construction-sidebar__body__section", isEditing && "editing")}>
            {buildingFootprints && (
                <FootprintsMap
                    key={buildingUid}
                    location={location}
                    buildingUid={selectedFootprintUid}
                    buildingFootprint={selectedFootprintPolygon}
                    buildingFootprints={buildingFootprints}
                    address={buildingConstruction.location}
                    setSelectedFootprintPolygon={setSelectedFootprintPolygon}
                    isEditing={isEditing}
                />
            )}
            {fields.map((field) => {
                if (field.type === "string" || field.type === "number")
                    return (
                        <BuildingPropertyInput
                            key={field.key}
                            value={
                                field.key === "defaultWeightNetPresentValue"
                                    ? (getValueForKey(field.key, buildingConstruction) ??
                                      (buildingConstruction.defaultWeightEmissionSavings != null
                                          ? 1 - buildingConstruction.defaultWeightEmissionSavings
                                          : undefined))
                                    : getValueForKey(field.key, buildingConstruction)
                            }
                            setValue={(value) => {
                                if (field.key === "defaultWeightEmissionSavings") {
                                    const emissionValue = value === "" || value === undefined ? null : value;
                                    const numericEmission =
                                        emissionValue !== null &&
                                        emissionValue !== undefined &&
                                        typeof emissionValue === "number"
                                            ? emissionValue
                                            : undefined;
                                    setProperties({
                                        defaultWeightEmissionSavings: numericEmission,
                                        defaultWeightNetPresentValue:
                                            numericEmission !== undefined ? 1 - numericEmission : undefined,
                                    });
                                } else {
                                    setProperties({
                                        [field.key]: value === "" || value === undefined ? null : value,
                                    });
                                }
                            }}
                            attributeKey={field.key}
                            editMode={isEditing}
                            type={field.type}
                            allowEdit={field.canEdit}
                            allowNegativeNumbers={field.allowNegativeNumber}
                            setValidationError={(value) => updateValidationMapForKey(field.key, value)}
                            required={field.required}
                            buildingConstruction={buildingConstruction}
                        />
                    );
                if (field.type === "select" && field.key === "buildingArchetype" && buildingConstruction[field.key])
                    return (
                        <BuildingTypeInput
                            key={field.key}
                            value={buildingConstruction[field.key]!}
                            setValue={(value) => setProperties({ [field.key]: value })}
                            editMode={isEditing}
                        />
                    );
                return null;
            })}
        </div>
    );
}
interface BuildingTypeInputProps {
    value: string;
    setValue: (value: string) => void;
    editMode: boolean;
}

function BuildingTypeInput({ value, setValue, editMode }: BuildingTypeInputProps) {
    const { humanReadable, formatFunction } = getDisplayInfo("buildingArchetype");

    if (!editMode)
        return (
            <div className="construction-sidebar__body__property">
                <Header size="small" style={{ color: "var(--audette-gray-500)" }}>
                    {humanReadable}:
                </Header>
                <Paragraph>{formatFunction(value)}</Paragraph>
            </div>
        );

    return <BuildingTypeDropdown value={value} setValue={setValue} required={true} />;
}

interface BuildingPropertyInputProps {
    value: number | string | undefined | null;
    setValue: (v: number | string | undefined) => void;
    attributeKey: string;
    editMode: boolean;
    type: "number" | "string";
    allowEdit?: boolean;
    allowNegativeNumbers?: boolean;
    required?: boolean;
    setValidationError: (isError: boolean) => void;
    buildingConstruction?: BuildingModelConstruction;
}

function BuildingPropertyInput({
    value,
    setValue,
    attributeKey,
    editMode,
    type,
    allowEdit = false,
    allowNegativeNumbers = true,
    setValidationError,
    required,
    buildingConstruction,
}: BuildingPropertyInputProps) {
    const { unit, humanReadable, formatFunction, recommendedRange } = getDisplayInfo(attributeKey);
    const showReadOnly = !allowEdit || !editMode;
    if (showReadOnly) {
        const renderValue = () => {
            if (valueIsValid(value)) {
                const isNpvWithEmission =
                    attributeKey === "defaultWeightNetPresentValue" &&
                    buildingConstruction?.defaultWeightEmissionSavings != null;
                return (
                    <Paragraph>
                        {isNpvWithEmission
                            ? `${formatFunction(value)}% (100% − ${formatFunction(buildingConstruction!.defaultWeightEmissionSavings!)}%)`
                            : `${formatFunction(value)}${unit === "%" ? "%" : ` ${unit}`}`}
                    </Paragraph>
                );
            }
            if (attributeKey === "defaultWeightNetPresentValue") {
                return <Paragraph style={{ color: "var(--audette-gray-500)" }}>—</Paragraph>;
            }
            return <ErrorIcon color="var(--audette-gray-400)" />;
        };
        return (
            <div className="construction-sidebar__body__property">
                <Header size="small" style={{ color: "var(--audette-gray-500)" }}>
                    {humanReadable}:
                </Header>
                {renderValue()}
                {recommendedRange &&
                    !(
                        attributeKey === "defaultWeightNetPresentValue" &&
                        value != null &&
                        typeof value === "number" &&
                        buildingConstruction?.defaultWeightEmissionSavings != null
                    ) && (
                        <Paragraph size="small" style={{ color: "var(--audette-gray-500)", marginTop: "4px" }}>
                            {recommendedRange}
                        </Paragraph>
                    )}
            </div>
        );
    }

    return (
        <FormInput
            value={value ?? ""}
            setValue={setValue}
            attributeKey={attributeKey}
            type={type}
            allowNegativeNumbers={allowNegativeNumbers}
            setIsError={setValidationError}
            required={required}
        />
    );
}

export default BuildingSectionBody;
